import {useCallback, useMemo} from 'react'

import type {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import {MemexActionType, MemexWorkflowTriggerType} from '../api/workflows/contracts'
import {isWorkflowPersisted} from '../helpers/workflow-utilities'
import type {ColumnModelForDataType} from '../models/column-model'
import {useWorkflows} from '../state-providers/workflows/use-workflows'

/**
 * Hook to read and update the default value for the Status field.
 *
 * The Status field uses the "Item added to project" workflow as its backing
 * store for the default value rather than the column settings. This hook
 * finds that workflow, exposes the currently-configured option id and the
 * workflow's enabled state, and provides a callback that updates the workflow
 * (enabling it if necessary) when the user picks a new default value.
 */
export function useStatusDefaultValue(column: ColumnModelForDataType<typeof MemexColumnDataType.SingleSelect>) {
  const {workflows, updateWorkflowAndAction} = useWorkflows()

  // Find the "Item added to project" workflow that has a set_field action
  // targeting the status column.
  const itemAddedWorkflow = useMemo(() => {
    return workflows.find(
      workflow =>
        workflow.triggerType === MemexWorkflowTriggerType.ItemAdded &&
        workflow.actions.some(
          action => action.actionType === MemexActionType.SetField && action.arguments.fieldId === column.databaseId,
        ),
    )
  }, [workflows, column.databaseId])

  const setFieldAction = useMemo(() => {
    return itemAddedWorkflow?.actions.find(
      action => action.actionType === MemexActionType.SetField && action.arguments.fieldId === column.databaseId,
    )
  }, [itemAddedWorkflow, column.databaseId])

  /** The option id currently configured as the default value, or undefined. */
  const defaultValueOptionId = setFieldAction?.arguments.fieldOptionId

  /** Whether the underlying workflow is enabled (i.e. the default value is active). */
  const isWorkflowEnabled = itemAddedWorkflow?.enabled ?? false

  /**
   * Update the default value for the status field.
   *
   * - When `optionId` is provided it updates the workflow's set_field action and
   *   enables the workflow if it was previously disabled.
   * - When `optionId` is undefined it disables the workflow (clears the default).
   */
  const setDefaultValue = useCallback(
    async (optionId: string | undefined) => {
      if (!itemAddedWorkflow) return

      if (optionId === undefined) {
        // User cleared the default value — disable the workflow.
        // Use updateWorkflowAndAction with skipNavigation:true so the user stays
        // on the field settings page instead of being redirected to the workflow editor.
        if (isWorkflowPersisted(itemAddedWorkflow) && itemAddedWorkflow.enabled) {
          await updateWorkflowAndAction({...itemAddedWorkflow, enabled: false}, [], {skipNavigation: true})
        }
        return
      }

      // Build the updated set_field action with the new option.
      const updatedAction = {
        ...setFieldAction,
        actionType: MemexActionType.SetField,
        arguments: {
          fieldId: column.databaseId,
          fieldOptionId: optionId,
        },
      }

      // Pass the workflow with enabled: true so that the single update call
      // both changes the action and enables the workflow when needed.
      // skipNavigation prevents the workflow editor page from opening.
      const workflowWithEnabled = {...itemAddedWorkflow, enabled: true}
      await updateWorkflowAndAction(workflowWithEnabled, [updatedAction], {skipNavigation: true})
    },
    [itemAddedWorkflow, setFieldAction, column.databaseId, updateWorkflowAndAction],
  )

  return {
    defaultValueOptionId,
    isWorkflowEnabled,
    itemAddedWorkflow,
    setDefaultValue,
  }
}
